import { useEffect, useRef } from "react";
import styled from "styled-components";
import { Box, Flex, Image, Text } from "rebass/styled-components";
import videojs from "video.js";
import { useHistory } from "react-router-dom";

import { IFeaturedItem } from "../@types";
import { analyticsClickEvent } from "MVVM/Components/Overlays/utils";
import { VideoPlayer } from "MVVM/VideoPlayer";
import {
  OverlayBadge,
  OverlayButton,
  OverlayTag,
} from "../../../../Components/Overlays";
import { FeaturedUser } from "../../FeaturedUser";
import theme from "theme/themeThree";

const FeaturedItem = ({
  item,
  active,
  hovered,
}: {
  item: IFeaturedItem;
  active: boolean;
  hovered?: boolean;
}) => {
  const videoPlayerRef = useRef<videojs.Player | null>(null);
  const history = useHistory();

  useEffect(() => {
    if (!videoPlayerRef?.current) return;

    active
      ? videoPlayerRef.current.play()?.catch(() => {})
      : videoPlayerRef.current.pause();
  }, [active]);

  return (
    <FeaturedItemWrapper className="featured-item">
      <VideoPlayerRounder>
        <VideoContainer
          className="video-container"
          onClick={() => {
            analyticsClickEvent(item.type);

            if (!item.link.length) return;

            if (["motw", "cotd"].includes(item.type))
              return history.push({
                pathname: item.link,
                state: { autoplay: true, referrer: "/" },
              });

            if (item.shareId)
              return history.push({
                pathname: `clip/${item.shareId}${
                  item.promotionName ? `?promotion=${item.promotionName}` : ""
                }`,
                state: { autoplay: true, referrer: "/" },
              });

            if (item.external) return window.open(item.link);

            return history.push(item.link);
          }}
        >
          <Image
            src={item.thumbnailUrl}
            width="100%"
            style={{
              position: "absolute",
              zIndex: 1,
              borderTopLeftRadius: 9,
              borderBottomLeftRadius: 9,
              transition: "opacity 0.5s ease-in-out",
              opacity: `${active ? 0 : 1}`,
            }}
          />
          <Box
            style={{
              transition: "opacity 0.5s ease-in-out",
              opacity: `${active ? 1 : 0}`,
              zIndex: 2,
            }}
          >
            <OverlayBadge type={item.type} />
            <VideoPlayer
              refForVideoPlayer={videoPlayerRef}
              mp4Source={item.url}
              loop={true}
              eventSrc="Carousel"
              playAnalyticsProperties={{
                shareId: item.shareId,
                source: "Homepage Featured",
              }}
              watchAnalyticsProperties={{
                action: "watch",
                collection: "Homepage featured",
                collectionType: "Generic",
                item: item.shareId,
                itemType: "Clip",
              }}
              options={{
                aspectRatio: "16:9",
                autoplay: active ? "muted" : false,
                controls: false,
                fill: true,
                fluid: true,
                muted: true,
                playsinline: true,
                poster: item.thumbnailUrl,
                responsive: true,
                userActions: {
                  click: false,
                },
              }}
            />

            {active && (
              <ProgressBar active={active} hovered={hovered || false} />
            )}
          </Box>
        </VideoContainer>
      </VideoPlayerRounder>
      <VideoSection>
        <Flex flexDirection="column">
          <Flex
            flexDirection={["row-reverse", "row-reverse", "column"]}
            justifyContent="space-between"
            mr={[4, 4, 0]}
          >
            <OverlayTag
              tag={item.tag}
              style={{
                alignSelf: "flex-start",
                borderRadius: "20px",
                display: "inline-block",
                fontSize: "0.7em",
                margin: "0 0 12px 0",
                textWrap: "nowrap",
              }}
            />
            <SectionTitle variant="text.title2" mb={3}>
              {item.type === "cotd"
                ? "Clip of the Day"
                : item.type === "motw"
                ? "Montage of the Week"
                : item.title}{" "}
            </SectionTitle>
          </Flex>
          <MobileHide>
            {["motw", "cotd"].includes(item.type) && (
              <FeaturedUser
                avatar={item.avatar}
                createdAt={item.createdAt}
                userId={item.userId}
                username={item.username}
                views={item.views}
              />
            )}
          </MobileHide>
        </Flex>
        <OverlayButton
          buttonCopy={
            item.type === "cotd"
              ? "Go To Clip of the Day!"
              : item.type === "motw"
              ? "Check out the MotW!"
              : item.buttonCopy
          }
          external={item.external}
          link={item.link}
          name={item.title}
          type={item.type}
          buttonAction={item?.buttonAction}
          style={{
            borderRadius: "12px",
            fontWeight: 700,
            lineHeight: "1em",
            marginTop: "auto",
            position: "static",
            minHeight: "20px",
          }}
        />
      </VideoSection>
    </FeaturedItemWrapper>
  );
};

const ProgressBar = styled(Box)<{ active: boolean; hovered: boolean }>`
  background: linear-gradient(
    90deg,
    ${theme.colors.envy} 0%,
    ${theme.colors.envy} 100%
  );
  bottom: 0;
  height: 3px;
  left: 0;
  position: absolute;
  width: 0%;
  z-index: 10;

  ${({ active, hovered }) =>
    active &&
    !hovered &&
    `
    animation: progressFill 5.7s linear forwards;
  `}

  ${({ hovered }) =>
    hovered &&
    `
    animation: none;
    width: 0%;
  `}

  @keyframes progressFill {
    from {
      width: 0%;
    }
    to {
      width: 100%;
    }
  }
`;

const FeaturedItemWrapper = styled(Flex)`
  background-color: ${({ theme }) => theme.colors.darkerNed};
  border-radius: 12px;
  height: 234px;
  width: 672px;

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    flex-direction: column;
    margin: 0 auto;
    width: 100vw;
    height: auto;
  }
`;

const VideoPlayerRounder = styled(Box)`
  position: relative;

  .video-js,
  .vjs-poster,
  .vjs-tech,
  .vjs-modal-dialog-content,
  .vjs-error-display {
    border-bottom-left-radius: 8px;
    border-top-left-radius: 8px;

    @media (max-width: ${(props) => props.theme.breaks.small}) {
      border-radius: 0;
    }
  }

  .video-js .vjs-control-bar {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`;

const VideoSection = styled(Flex)`
  background-color: ${({ theme }) => theme.colors.darkerNed};
  flex-direction: column;
  justify-content: space-around;
  padding: 24px;
  width: 240px;

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    padding: 18px;
    width: 100%;
  }
`;

const MobileHide = styled(Box)`
  @media (max-width: ${(props) => props.theme.breaks.small}) {
    display: none;
  }
`;

const SectionTitle = styled(Text)`
  font-weight: 700;

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    font-size: ${({ theme }) => theme.fontSizes[3]};
    margin-top: -2px;
    margin-bottom: 20px;
    max-width: 70%;
  }
`;

const VideoContainer = styled(Box)`
  cursor: pointer;
  height: 234px;
  width: 416px;

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    height: auto;
    width: 100%;
  }
`;

export { FeaturedItem };
